// [VexFlow](https://vexflow.com) - Copyright (c) Mohit Muthanna 2010.
// Author: Ron B. Yeh
// MIT License
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Use instead of `instanceof` as a more flexible type guard.
 * @param obj check if this object's CATEGORY matches the provided category.
 * @param category a string representing a category of VexFlow objects.
 * @param checkAncestors defaults to `true`, so we walk up the prototype chain to look for a matching `CATEGORY`.
 *        If `false`, we do not check the superclass or other ancestors.
 * @returns true if `obj` has a static `CATEGORY` property that matches `category`.
 */
export function isCategory(obj, category, checkAncestors = true) {
    // obj is undefined, a number, a primitive string, or null.
    if (typeof obj !== 'object' || obj === null) {
        return false;
    }
    // `obj.constructor` is a reference to the constructor function that created the `obj` instance.
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/constructor
    let constructorFcn = obj.constructor;
    // Check if the object's static .CATEGORY matches the provided category.
    if (checkAncestors) {
        // Walk up the prototype chain to look for a matching obj.constructor.CATEGORY.
        while (obj !== null) {
            constructorFcn = obj.constructor;
            if ('CATEGORY' in constructorFcn && constructorFcn.CATEGORY === category) {
                return true;
            }
            obj = Object.getPrototypeOf(obj);
        }
        return false;
    }
    else {
        // Do not walk up the prototype chain. Just check this particular object's static .CATEGORY string.
        return 'CATEGORY' in constructorFcn && constructorFcn.CATEGORY === category;
    }
}
export const isAccidental = (obj) => isCategory(obj, Category.Accidental);
export const isAnnotation = (obj) => isCategory(obj, Category.Annotation);
export const isBarline = (obj) => isCategory(obj, Category.Barline);
export const isDot = (obj) => isCategory(obj, Category.Dot);
export const isGraceNote = (obj) => isCategory(obj, Category.GraceNote);
export const isGraceNoteGroup = (obj) => isCategory(obj, Category.GraceNoteGroup);
export const isNote = (obj) => isCategory(obj, Category.Note);
export const isRenderContext = (obj) => isCategory(obj, Category.RenderContext);
export const isStaveNote = (obj) => isCategory(obj, Category.StaveNote);
export const isStemmableNote = (obj) => isCategory(obj, Category.StemmableNote);
export const isTabNote = (obj) => isCategory(obj, Category.TabNote);
// 'const' enums are erased by the TypeScript compiler. The string values are inlined at all the use sites.
// See: https://www.typescriptlang.org/docs/handbook/enums.html#const-enums
export var Category;
(function (Category) {
    Category["Accidental"] = "Accidental";
    Category["Annotation"] = "Annotation";
    Category["Articulation"] = "Articulation";
    Category["Barline"] = "Barline";
    Category["BarNote"] = "BarNote";
    Category["Beam"] = "Beam";
    Category["Bend"] = "Bend";
    Category["ChordSymbol"] = "ChordSymbol";
    Category["Clef"] = "Clef";
    Category["ClefNote"] = "ClefNote";
    Category["Crescendo"] = "Crescendo";
    Category["Curve"] = "Curve";
    Category["Dot"] = "Dot";
    Category["Element"] = "Element";
    Category["Fraction"] = "Fraction";
    Category["FretHandFinger"] = "FretHandFinger";
    Category["GhostNote"] = "GhostNote";
    Category["Glyph"] = "Glyph";
    Category["GlyphNote"] = "GlyphNote";
    Category["GraceNote"] = "GraceNote";
    Category["GraceNoteGroup"] = "GraceNoteGroup";
    Category["GraceTabNote"] = "GraceTabNote";
    Category["KeySignature"] = "KeySignature";
    Category["KeySigNote"] = "KeySigNote";
    Category["Modifier"] = "Modifier";
    Category["MultiMeasureRest"] = "MultiMeasureRest";
    Category["Note"] = "Note";
    Category["NoteHead"] = "NoteHead";
    Category["NoteSubGroup"] = "NoteSubGroup";
    Category["Ornament"] = "Ornament";
    Category["Parenthesis"] = "Parenthesis";
    Category["PedalMarking"] = "PedalMarking";
    Category["RenderContext"] = "RenderContext";
    Category["RepeatNote"] = "RepeatNote";
    Category["Repetition"] = "Repetition";
    Category["Stave"] = "Stave";
    Category["StaveConnector"] = "StaveConnector";
    Category["StaveHairpin"] = "StaveHairpin";
    Category["StaveLine"] = "StaveLine";
    Category["StaveModifier"] = "StaveModifier";
    Category["StaveNote"] = "StaveNote";
    Category["StaveSection"] = "StaveSection";
    Category["StaveTempo"] = "StaveTempo";
    Category["StaveText"] = "StaveText";
    Category["StaveTie"] = "StaveTie";
    Category["Stem"] = "Stem";
    Category["StemmableNote"] = "StemmableNote";
    Category["StringNumber"] = "StringNumber";
    Category["Stroke"] = "Stroke";
    Category["System"] = "System";
    Category["TabNote"] = "TabNote";
    Category["TabSlide"] = "TabSlide";
    Category["TabStave"] = "TabStave";
    Category["TabTie"] = "TabTie";
    Category["TextBracket"] = "TextBracket";
    Category["TextDynamics"] = "TextDynamics";
    Category["TextNote"] = "TextNote";
    Category["Tickable"] = "Tickable";
    Category["TimeSignature"] = "TimeSignature";
    Category["TimeSigNote"] = "TimeSigNote";
    Category["Tremolo"] = "Tremolo";
    Category["Tuplet"] = "Tuplet";
    Category["Vibrato"] = "Vibrato";
    Category["VibratoBracket"] = "VibratoBracket";
    Category["Voice"] = "Voice";
    Category["Volta"] = "Volta";
})(Category || (Category = {}));
